import './styles.scss';

import { Button, Form } from 'react-bootstrap';
import { DtrParsedTrip, PotentialLocation } from '@/models/gen/graphql';
import { formatFullAddress, formatLocationKey } from '@/features/DriveTimeTool/utils/locations';
import useConfirmation, { Confirmation } from '@/hooks/useConfirmation';
import { useEffect, useMemo } from 'react';

import LocationInput from '@/features/DriveTimeTool/components/LocationInput';
import Modal from '@/common/Modal';
import TippyWhen from '@/components/TippyWhen';
import { createComponentState } from '@/state';

const confirmationOptions: Confirmation.Options = {
  Body: { title: 'Are You Sure?', message: 'You have unsaved changes that will be lost. Are you sure?' },
};

export type MissingLocationWithAirport = PotentialLocation & { airportCode: string };
type ResolvedLocation = { name: string; address: string; airportCode: string };
type Props = { onSubmit: (resolved: ResolvedLocation[]) => Promise<void> };
export type LocationReconciliationModalState = {
  show: boolean;
  loading: boolean;
  routes: Map<string, DtrParsedTrip[]>;
  missingLocations: Array<MissingLocationWithAirport>;
  resolvedLocations: { name: string; address: string; airportCode: string }[];
  skippedLocations: string[]; // store locationKey `${name}:${airportCode}` when skipping
  currentLocationIndex: number | null;
  address: string;
};
const initLocationReconciliationModalState: () => LocationReconciliationModalState = () => ({
  show: false,
  loading: false,
  routes: new Map(),
  missingLocations: [],
  resolvedLocations: [],
  skippedLocations: [],
  currentLocationIndex: null,
  address: '',
});
export const useLocationReconciliationModal = createComponentState<LocationReconciliationModalState>(
  initLocationReconciliationModalState()
);

const LocationReconciliationModal = ({ onSubmit }: Props) => {
  const [state, setState] = useLocationReconciliationModal(({ state, setState }) => [state, setState]);
  const onConfirm = useConfirmation(confirmationOptions);

  const handleHide = async (): Promise<void> => {
    try {
      if (state.resolvedLocations.length + state.skippedLocations.length !== state.missingLocations.length) await onConfirm();
      setState((current) => ({ ...current, ...initLocationReconciliationModalState() }));
    } catch (err) {}
  };

  const onSaveAndContinue = (): void => {
    setState((current) => {
      if (current.address === '' || current.resolvedLocations.length + current.skippedLocations.length === current.missingLocations.length)
        return current;

      const resolvedLocations = [...current.resolvedLocations];
      resolvedLocations.push({ name: location.name, address: current.address, airportCode: location.airportCode });
      const nextLocation = current.missingLocations[(state.currentLocationIndex || 0) + 1];
      return {
        ...current,
        resolvedLocations,
        currentLocationIndex: current.currentLocationIndex + 1,
        address: formatFullAddress(nextLocation) || '',
      };
    });
  };
  const onSaveAndReturn = (): void => {
    setState((current) => {
      if (current.address === '') return current;
      const resolvedLocations = [...current.resolvedLocations];
      resolvedLocations.push({ name: location.name, address: current.address, airportCode: location.airportCode });
      if (onSubmit) onSubmit(resolvedLocations);
      return { ...current, ...initLocationReconciliationModalState() };
    });
  };

  const onSkipThisLocation = async (): Promise<void> => {
    setState((current) => {
      const skippedLocations = [...current.skippedLocations];
      skippedLocations.push(formatLocationKey(location.name, location.airportCode));
      const nextLocation = current.missingLocations[(state.currentLocationIndex || 0) + 1];
      if (!nextLocation) {
        if (onSubmit) onSubmit(current.resolvedLocations);
        return { ...current, ...initLocationReconciliationModalState() };
      }
      return {
        ...current,
        skippedLocations,
        currentLocationIndex: current?.currentLocationIndex + 1,
        address: formatFullAddress(nextLocation) || '',
      };
    });
  };

  const location = useMemo<MissingLocationWithAirport | null>(
    () => (state.currentLocationIndex === null ? null : state.missingLocations[state.currentLocationIndex] || null),
    [state.missingLocations, state.currentLocationIndex]
  );
  const routesAffected = useMemo(() => {
    if (!location) return 0;
    return Array.from(state.routes.keys()).filter((key) => key.includes(formatLocationKey(location.name, location.airportCode))).length;
  }, [state.routes, location]);

  useEffect(() => {
    if (!state.show) return;
    if (state.missingLocations.length === state.resolvedLocations.length + state.skippedLocations.length) {
      onSaveAndReturn();
      return;
    }
    const addressInput = document.querySelector('.LocationReconciliationModal input[name="address"]') as HTMLInputElement;
    if (!addressInput) return;
    // focus and highlight text
    addressInput.value = formatFullAddress(location) || location?.name || '';
    addressInput.focus();
    addressInput.select();
  }, [state.show, state.currentLocationIndex]);

  const Header = () => (
    <div className="d-flex align-items-center flex-nowrap">
      <i className="sv sv-location fs-3 text-light bg-purple-gradient p-3 me-3 rounded-3 d-flex align-items-center justify-content-center" />
      <div className="d-flex flex-column justify-content-center">
        <h3 className="fw-normal">LOCATION RECONCILIATION</h3>
        <p className="text-muted m-0">Map unknown locations or skip to exclude from import</p>
      </div>
    </div>
  );

  const Footer = () => (
    <div className="d-flex align-items-center justify-content-between w-100 gap-3 m-0">
      {state.currentLocationIndex + 1 !== state.missingLocations.length && (
        <TippyWhen isTrue={!state.address} options={{ content: 'Please enter a valid address to proceed.' }}>
          <div className="w-100">
            <Button
              disabled={!state.address}
              variant="success"
              className="btn-shadow w-100 h-100 d-flex align-items-center justify-content-center py-2"
              onClick={onSaveAndContinue}
            >
              <i className="sv sv-floppy-disk me-2 fs-4" />
              Save & Continue
            </Button>
          </div>
        </TippyWhen>
      )}
      <TippyWhen isTrue={!state.address} options={{ content: 'Please enter a valid address to proceed.' }}>
        <div className="w-100">
          <Button
            disabled={!state.address}
            variant="primary"
            className="btn-shadow w-100 h-100 d-flex align-items-center justify-content-center py-2"
            onClick={onSaveAndReturn}
          >
            <i className="sv sv-floppy-disk me-2 fs-4" />
            Save & Return
          </Button>
        </div>
      </TippyWhen>
      <div className="w-100">
        <Button
          variant="light"
          className="btn-shadow w-100 h-100 d-flex align-items-center justify-content-center py-2"
          onClick={onSkipThisLocation}
        >
          <i className="sv sv-arrow-right-square me-2 fs-4" />
          Skip This Location
        </Button>
      </div>
    </div>
  );

  return (
    <Modal className="LocationReconciliationModal" show={state.show} size="sm" header={Header} footer={Footer} onHide={handleHide} centered>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <p className="m-0">
          {location?.name || ''} {`(${location?.airportCode || ''})`} ;
          <strong className="text-primary ">Found {routesAffected} times</strong> as pickup/dropoff
        </p>
        <span className="text-primary bg-cyan-subtle border border-cyan border-1 rounded-3 py-2 px-3 d-flex align-items-center">
          <i className="sv sv-clock3 fs-4 me-2" /> {state.resolvedLocations.length + state.skippedLocations.length}/
          {state.missingLocations.length} Processed
        </span>
      </div>

      <div className="bg-cyan-subtle border border-1 border-cyan p-4 rounded-3 fw-normal">
        <h5 className="d-flex align-items-center mb-2 text-dark fw-normal">
          <i className="sv sv-plus text-success fs-4 me-2" />
          CREATE LOCATION
        </h5>
        <p className="text-muted">Create a new location with proper address</p>
        <div className="d-flex flex-column w-100 gap-3">
          <Form.Group className="group-input">
            <Form.Label className="text-dark">Location Name:</Form.Label>
            <Form.Control type="text" name="name" value={location?.name || ''} disabled placeholder="Enter location name here..." />
          </Form.Group>
          <Form.Group className="group-input">
            <Form.Label className="text-dark">Full Address:</Form.Label>
            <LocationInput
              name="address"
              placeholder="Address"
              onChange={(value) => setState((current) => ({ ...current, address: value }))}
              defaultValue={formatFullAddress(location) || location?.name || ''}
              // ref={locationInputRef}
            />
          </Form.Group>
        </div>
      </div>
    </Modal>
  );
};

export default LocationReconciliationModal;
